import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Collapse,
  Group,
  Loader,
  Paper,
  Progress,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { Dropzone, FileRejection, FileWithPath } from '@mantine/dropzone';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconCloudUpload, IconHelp, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileStatus } from '@mygpt/app';

import CheckToChat from '@/components/Create_V2/CheckToChat';
import { SvgFileDragAdd, SvgUploadDocumentFile } from '@/components/Create_V2/components/Documents/icon';
import { TaskDocument, useTaskDocument } from '@/components/Create_V2/components/Documents/useTaskDocument';
import Description from '@/components/Create_V2/Description';
import { ModelSelect } from '@/components/Create_V2/ModelSelect';
import FileIcon from '@/components/FileIcon';
import { useGPTStore } from '@/store';
import { useCreateStore } from '@/store/create';
import { isFileStatusReady } from '@/utils/embed';
import { formatFileSize } from '@/utils/helper';
import { queuePromises } from '@/utils/task';

export default () => {
  const { t } = useTranslation();

  const theme = useMantineTheme();
  const { creating, pageType, setDatasetId, toggle } = useCreateStore();
  const [detailOpened, { toggle: detailToggle }] = useDisclosure(false);

  const MaxFiles = +import.meta.env.VITE_MAX_FILES || 50;
  // 单文件 / 单次累计上传大小上限优先级：
  //   admin override (超级管理员针对该用户单独设置) > 环境变量 > 默认 100 MB
  // userInfo 由 layout 在 app mount 时拉取 /users/me 并缓存到 GPT store，
  // admin_overrides 通过 User model 字段自动随响应返回，这里零额外请求。
  const userInfo = useGPTStore((state) => state.userInfo);
  const overrideMaxFileSizeMb = userInfo?.admin_overrides?.max_file_size_mb;
  const MaxFileSize =
    typeof overrideMaxFileSizeMb === 'number' && overrideMaxFileSizeMb > 0
      ? overrideMaxFileSizeMb
      : +import.meta.env.VITE_MAX_TOTAL_FILES_SIZE || 100;

  const openRef = useRef<() => void>(null);

  const [files, setFiles] = useState<FileWithPath[]>([]);
  const { documents, percent, tasks } = useTaskDocument(files);

  const [step, setStep] = useState(0);
  const [failMessage, setFailMessage] = useState('');

  const currentFileSize = useMemo(() => {
    let size = 0;
    files.forEach((item) => {
      size = size + item.size;
    });
    return +formatFileSize(size, false, 'MB');
  }, [files]);

  const acceptedFiles = (acceptedFiles: FileWithPath[]) => {
    // 过滤重复名文件, 且判断总大小
    const nameSameFiles: FileWithPath[] = [];
    const addFiles: FileWithPath[] = [];
    let totalSize = 0;

    acceptedFiles.map((item) => {
      const fileSizeMB = +formatFileSize(item.size, false, 'MB');
      if (fileSizeMB > MaxFileSize) {
        showNotification({
          message: t('errors.detail.single_file_size_limit', { size: MaxFileSize }),
          color: 'red',
        });
        return;
      }
      totalSize = totalSize + item.size;
      if (files.findIndex((itemFiles) => itemFiles.name === item.name) === -1) {
        addFiles.push(item);
      } else {
        nameSameFiles.push(item);
      }
    });

    if (+formatFileSize(totalSize, false, 'MB') + currentFileSize > MaxFileSize) {
      showNotification({
        message: t('errors.detail.upload_size_limit'),
        color: 'red',
      });
      addFiles.splice(0, addFiles.length);
    }

    if (files.length + addFiles.length > MaxFiles) {
      showNotification({
        message: t('errors.detail.upload_limit'),
        color: 'red',
      });
      addFiles.splice(0, addFiles.length);
    }

    if (nameSameFiles.length) {
      showNotification({
        message: t('create_v2.documents.same_name'),
        color: 'red',
      });
    }

    setFiles((filesArr) => [...filesArr, ...addFiles]);
  };

  const rejectedFiles = (fileRejections: FileRejection[]) => {
    console.log('rejected files', fileRejections);
    if (fileRejections.length > MaxFiles) {
      showNotification({
        message: t('errors.detail.upload_limit'),
        color: 'red',
      });
    } else {
      fileRejections.map((item) => {
        const { errors, file } = item;
        const fileName = file.name;

        const fileError = errors.map((error) => {
          switch (error.code) {
            case 'file-invalid-type':
              return t('create_v2.documents.file_type_error');
            case 'file-too-large':
              return t('errors.detail.file_large');
            default:
              return error.message;
          }
        });
        showNotification({
          message: (
            <>
              <Text>{fileName}</Text>
              <Text>{fileError}</Text>
            </>
          ),
          color: 'red',
        });
      });
    }
  };

  const deleteFile = (index: number) => {
    setFiles((arr) => arr.filter((_, itemIndex) => itemIndex !== index));
  };

  const submit = () => {
    toggle();
    const firstParam = {};
    const successCallback = (index: number) => {
      setStep(index);
    };
    const failedCallback = (index: number, error: string) => {
      setFailMessage(error);
      // setTotalSteps((arr) => [...arr.slice(0, index - 1), error, ...arr.slice(index)]);
    };
    queuePromises(firstParam, tasks, successCallback, failedCallback);
  };

  const retryTask = () => {
    pageType === 'create' && setDatasetId('');
    setStep(0);
    setFailMessage('');
    toggle();
  };

  const renderUploading = () => {
    return (
      <Progress.Root size="xl">
        <Progress.Section value={percent} striped animated>
          <Progress.Label className="font-normal">
            {t('create_v2.documents.uploading', {
              count: files.length,
              percent: percent,
            })}
          </Progress.Label>
        </Progress.Section>
      </Progress.Root>
    );
  };

  const chekcFileStatusComplete = (status: FileStatus) => {
    return status === 'complete';
  };

  const checkFileStatusProcess = (status: FileStatus) => {
    return status === 'process';
  };

  const checkFileStatusFail = (status: FileStatus) => {
    return status === 'fail' || status === 'exceeded';
  };

  const translateFailedMessage = (document: TaskDocument) => {
    const message = document.message || '';
    if (message.includes('exceeds the maximum allowed rows (10,000)')) {
      return t('errors.files.maximum_rows', { name: document.name });
    }
    if (message.includes('exceeds the maximum allowed columns (100).')) {
      return t('errors.files.maximum_columns', { name: document.name });
    }
    if (message.includes('Importing Excel files with merged cells is not allowed')) {
      return t('errors.files.not_merged_cells', { name: document.name });
    }
    if (message.includes('The PDF file is encrypted. We do not support encrypted PDF files')) {
      return t('errors.files.file_encrypted');
    }
    return `Failed${document.message ? ` - ${document.message}` : ''}`;
  };

  const renderTaskList = () => {
    return (
      <Stack className="text-xs">
        {documents.map((document) => {
          return (
            <Group key={document.id} justify="space-between" wrap="nowrap">
              <div className="flex-auto align-text-bottom min-w-0 break-all">
                <Tooltip color="gray.6" label={document.name} withinPortal={false} multiline>
                  <span>
                    {document.name} ({formatFileSize(document.file_size)})
                  </span>
                </Tooltip>
                {checkFileStatusFail(document.status) && (
                  <Text className="text-[13px]" c="red.5">
                    {translateFailedMessage(document)}
                  </Text>
                )}
              </div>
              {chekcFileStatusComplete(document.status) && (
                <Badge color="teal" className="text-xs font-normal">
                  {t('embeddings.table.filter_by_status.learned')}
                </Badge>
              )}
              {isFileStatusReady(document.status) && (
                <Badge color="gray">{t('embeddings.table.filter_by_status.ready')}</Badge>
              )}
              {checkFileStatusProcess(document.status) && (
                <Badge color="blue" className="text-xs font-normal" rightSection={<Loader color="white" size={10} />}>
                  {t('embeddings.table.filter_by_status.learning')}
                  {document.tokens ? `(${t('embeddings.table.tokens')} ${document.tokens})` : ''}
                </Badge>
              )}
              {checkFileStatusFail(document.status) && (
                <Badge color="red" className="text-xs font-normal" rightSection={<IconHelp color="white" size={14} />}>
                  {t('embeddings.table.filter_by_status.failed')}
                </Badge>
              )}
            </Group>
          );
        })}
      </Stack>
    );
  };

  const renderSection = () => {
    if (failMessage)
      return (
        <Text c="red.5" className="text-xs">
          {failMessage}
        </Text>
      );
    if (step === 1) return renderUploading();
    if (step === 2 || step === 3) return renderTaskList();
  };

  return (
    <section>
      <Stack>
        <Group justify="space-between">
          <h3 className="gray.8 font-medium">{t('create_v2.documents.label')}</h3>
          {!creating && files.length && (
            <Button
              className="group"
              variant="subtle"
              leftSection={
                <span
                  className={`p-1 rounded-full transition-all ${
                    files.length >= MaxFiles ? 'bg-gray-200' : 'bg-indigo-100 group-hover:bg-indigo-300'
                  }`}
                >
                  <IconPlus size="1rem" />
                </span>
              }
              onClick={() => openRef.current && openRef.current()}
              disabled={files.length >= MaxFiles || currentFileSize >= MaxFileSize}
            >
              {t('create_v2.documents.add_file')}
            </Button>
          )}
        </Group>

        <Collapse in={!creating}>
          <Collapse in={!files.length}>
            <section className="bg-white p-6 rounded-2xl shadow-lg">
              <Dropzone
                className="border-none bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100"
                openRef={openRef}
                onDrop={acceptedFiles}
                onReject={rejectedFiles}
                loading={false}
                maxFiles={MaxFiles}
                accept={{
                  'text/markdown': ['.md'],
                  'text/csv': ['.csv'],
                  'text/plain': ['.txt'],
                  // 'text/html': ['.html', '.htm'],
                  'application/pdf': ['.pdf'],
                  'application/vnd.ms-excel': ['.xls'],
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                  // 'application/rtf': ['.rtf'],
                  'application/msword': ['.doc'],
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                  'application/vnd.ms-powerpoint': ['.ppt'],
                  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
                  'image/png': ['.png'],
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/gif': ['.gif'],
                  'image/bmp': ['.bmp'],
                  'message/rfc822': ['.eml'],
                }}
              >
                <Stack className="flex items-center justify-center min-h-[180px] pointer-events-none" gap="xl">
                  <Dropzone.Accept>
                    <IconCloudUpload size="4rem" stroke={1.5} color={theme.colors[theme.primaryColor][6]} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconCloudUpload size="4rem" stroke={1.5} color={theme.colors[theme.primaryColor][6]} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <SvgFileDragAdd />
                  </Dropzone.Idle>

                  <div className="text-center">
                    <Text c="indigo" size="xl" fw={500} inline>
                      {t('create_v2.documents.upload_file_text')}
                    </Text>
                    <Text size="sm" c="gray.5" inline mt={10}>
                      {t('create_v2.documents.upload_file_desc')}
                    </Text>
                  </div>
                </Stack>
              </Dropzone>
            </section>
          </Collapse>

          <Collapse in={!!files.length}>
            <Paper shadow="md" radius="lg">
              <ul className="p-5 max-h-[40vh] overflow-auto">
                <Stack>
                  {files.map((item, index) => {
                    return (
                      <li key={item.name + item.lastModified} className="group p-2 rounded-md hover:bg-gray-50">
                        <Group justify="space-between" wrap="nowrap" className="flex-1">
                          <Group wrap="nowrap" className="flex-1 min-w-0">
                            <FileIcon fileName={item.name} />
                            <Text size={'lg'} className="flex-1 break-words overflow-hidden">
                              {item.name}
                            </Text>
                          </Group>
                          <section className="flex-shrink-0">
                            <Text className="h-[28px] group-hover:hidden">{formatFileSize(item.size)}</Text>
                            <ActionIcon
                              className={'hidden group-hover:flex'}
                              color="red"
                              onClick={() => {
                                deleteFile(index);
                              }}
                            >
                              <IconTrash size={'1rem'} />
                            </ActionIcon>
                          </section>
                        </Group>
                      </li>
                    );
                  })}
                </Stack>
              </ul>
            </Paper>
          </Collapse>

          <Group justify="space-between" className="relative mt-5 mb-3">
            <Group>
              <Text c="gray.5" size="xs" className="border rounded-xl py-1 px-2 bg-white border-violet-200">
                {t('create_v2.documents.files')}: <span className="text-violet-500">{files.length}</span> / {MaxFiles}
              </Text>
              <Text c="gray.5" size="xs" className="border rounded-xl py-1 px-2 bg-white border-violet-200">
                {t('create_v2.documents.file_size')}: <span className="text-violet-500">{currentFileSize}</span> MB /{' '}
                {MaxFileSize} MB
              </Text>
            </Group>

            <Description
              size="xs"
              text={t('create_v2.documents.description.text')}
              hoverText={t('create_v2.documents.description.detail')}
            />
          </Group>

          <ModelSelect />

          <Center className="mt-10">
            <Button size="md" onClick={submit} disabled={!files.length}>
              {t('create_v2.start_training')}
            </Button>
          </Center>
        </Collapse>

        <Collapse in={creating}>
          <section>
            <Paper shadow="md" radius="lg" p={'md'}>
              <Group justify="space-between">
                {files.length > 1 ? (
                  <>
                    <Group wrap="nowrap" className="min-w-0">
                      <SvgUploadDocumentFile />
                      <Text className="flex-1 break-words overflow-hidden">
                        {t('create_v2.documents.file_briefly', { name: files[0]?.name, length: files.length })}
                      </Text>
                    </Group>
                    <Button variant="subtle" onClick={detailToggle}>
                      {t('create_v2.documents.details')}
                    </Button>
                  </>
                ) : (
                  <Group wrap="nowrap" className="min-w-0">
                    <SvgUploadDocumentFile />
                    <Text className="flex-1 break-words overflow-hidden">{files[0]?.name}</Text>
                  </Group>
                )}
              </Group>

              <Collapse in={detailOpened} mt={15}>
                <section>
                  {files.map((item, index) => (
                    <Paper key={index} shadow="sm" radius="md" p="sm" withBorder mb={15}>
                      <Group justify="space-between">
                        <Group gap={10} wrap="nowrap" className="min-w-0">
                          <FileIcon fileName={item.name} />
                          <Text className="flex-1 break-words overflow-hidden">{item.name}</Text>
                        </Group>
                      </Group>
                    </Paper>
                  ))}
                </section>
              </Collapse>
            </Paper>
          </section>
          <section className="mt-10">
            <Stack>
              <h3 className="gray.8 font-medium">{t('create_v2.task_list')}</h3>
              <Paper className="p-5" shadow="md" radius="lg">
                {renderSection()}
              </Paper>
            </Stack>
          </section>

          <Center className="mt-10">
            {failMessage ? (
              <Button size="md" onClick={retryTask}>
                {t('errors.initialize.button')}
              </Button>
            ) : (
              <CheckToChat disabled={documents.length === 0 || ![2, 3].includes(step)} type="file" />
            )}
          </Center>
        </Collapse>
      </Stack>
    </section>
  );
};
